import { FC, useMemo } from "react";

import { Button, Card } from "react-bootstrap";
import { useForm, SubmitHandler } from "react-hook-form";
import { toast } from "react-toastify";

import { ExternalLink, Plus, Trash2, Cloud, Globe } from "lucide-react";

import Input from "@/common/components/form/Input";
import SelectForm from "@/common/components/form/SelectForm";
import { ExternalUrl, ExternalLinkType } from "@/common/interfaces";
import { useUpdateServiceExternalUrls } from "@/modules/order/hooks/service.hooks";

type ExternalUrlFormData = {
  name: string;
  url: string;
  type: ExternalLinkType | { value: ExternalLinkType; label: string };
};

type Props = {
  serviceId: string;
  externalUrls: ExternalUrl[];
  onExternalLinksUpdate?: (links: ExternalUrl[]) => void;
};

const generateVirtualId = () => `virtual_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

const ExternalFiles: FC<Props> = ({
  serviceId,
  externalUrls,
  onExternalLinksUpdate,
}) => {
  const externalUrlsWithIds = useMemo(() => {
    return externalUrls.map(url => ({
      ...url,
      id: url.id || generateVirtualId()
    }));
  }, [externalUrls]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid },
  } = useForm<ExternalUrlFormData>({
    defaultValues: {
      name: "",
      url: "",
      type: "other",
    },
    mode: "onChange",
  });

  const { mutate: updateExternalUrls, isPending: isLoading } = useUpdateServiceExternalUrls({
    handleSuccess: (updatedLinks) => {
      if (updatedLinks) {
        onExternalLinksUpdate?.(updatedLinks);
      }
      toast.success("Liens externes mis à jour avec succès");
    },
  });

  const linkTypeOptions = [
    { value: "drive", label: "Google Drive" },
    { value: "dropbox", label: "Dropbox" },
    { value: "onedrive", label: "OneDrive" },
    { value: "other", label: "Autre" },
  ];

  const getIconForType = (type: ExternalUrl["type"]) => {
    switch (type) {
      case "drive":
        return <Cloud className="w-4 h-4 text-blue-600" />;
      case "dropbox":
        return <Cloud className="w-4 h-4 text-blue-500" />;
      case "onedrive":
        return <Cloud className="w-4 h-4 text-blue-700" />;
      default:
        return <Globe className="w-4 h-4 text-gray-600" />;
    }
  };

  const onSubmit: SubmitHandler<ExternalUrlFormData> = async (data) => {
    const typeValue =
      typeof data.type === "string" ? data.type : data.type.value;

    const newLink = {
      id: generateVirtualId(),
      name: data.name,
      url: data.url,
      type: typeValue,
    };

    const updatedLinks = [...externalUrlsWithIds, newLink];
    updateExternalUrls({ serviceId, externalUrls: updatedLinks });
    reset();
  };

  const handleRemoveExternalLink = async (linkId: string) => {
    const updatedLinks = externalUrlsWithIds.filter((url) => url.id !== linkId);
    updateExternalUrls({ serviceId, externalUrls: updatedLinks });
  };

  const urlValidation = {
    required: "L'URL est requise",
    pattern: {
      value: /^https?:\/\/.+/,
      message: "L'URL doit commencer par http:// ou https://",
    },
  };

  return (
    <Card className="border border-gray-200 bg-white">
      <Card.Body className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <ExternalLink className="w-5 h-5 text-blue-600" />
          <span className="text-lg font-semibold text-gray-800">
            Liens externes
          </span>
        </div>
        <div className="space-y-3 mb-4">
          {externalUrlsWithIds.map((link) => (
            <div
              key={link.id}
              className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border"
            >
              {getIconForType(link.type as ExternalUrl["type"])}
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-800 m-1">{link.name}</p>
                <p className="text-sm text-gray-500 truncate m-1">{link.url}</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.open(link.url, "_blank")}
                className="text-blue-600 hover:text-blue-700"
                disabled={isLoading}
              >
                <ExternalLink className="w-4 h-4 mr-1" />
                Ouvrir
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleRemoveExternalLink(link.id!)}
                className="text-red-500 hover:text-red-700"
                disabled={isLoading}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-center gap-2 mb-2">
              <Plus className="w-4 h-4 text-blue-600" />
              <span className="font-medium text-blue-800">
                Ajouter un lien externe
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Input
                  control={control as any}
                  label="Nom du lien"
                  name="name"
                  placeholder="ex: Plan architectural"
                  rules={{ required: "Le nom du lien est requis" }}
                />
              </div>
              <div>
                <Input
                  control={control as any}
                  label="URL du lien"
                  name="url"
                  placeholder="https://drive.google.com/..."
                  rules={urlValidation}
                />
              </div>
              <div>
                <SelectForm
                  control={control as any}
                  label="Type de lien"
                  name="type"
                  options={linkTypeOptions}
                  rules={{ required: "Le type de lien est requis" }}
                />
              </div>
            </div>
            <Button
              type="submit"
              disabled={!isValid || isLoading}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {isLoading ? (
                <>
                  <div
                    className="spinner-border spinner-border-sm mr-2"
                    role="status"
                  >
                    <span className="sr-only">Loading...</span>
                  </div>
                  Ajout en cours...
                </>
              ) : (
                <>
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Ajouter le lien
                </>
              )}
            </Button>
          </div>
        </form>
      </Card.Body>
    </Card>
  );
};

export default ExternalFiles;
